import { FormattedMessage } from 'react-intl';
import { animated, easings, useSpring } from 'react-spring';
import styled, { keyframes } from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { CornerBorderedBox } from '@sorare/core/src/atoms/ui/CornerBorderedBox';
import { useIntlContext } from '@sorare/core/src/contexts/intl';

const scoreScaleIn = keyframes`
  0% {
    bottom: 100%;
    transform: translateX(-50%) scale(0);
  }
  20% {
    bottom: 50%;
    transform: translateX(-50%) translateY(50%) scale(1);
  }
  80% {
    bottom: 50%;
    transform: translateX(-50%) translateY(50%) scale(1);
  }
  100% {
    bottom: 10%;
    transform: translateX(-50%) translateY(-50%) scale(1);
  }
`;
const rewardScaleIn = keyframes`
  0% {
    bottom: 100%;
    transform: translateX(-50%) scale(0);
  }
  100% {
    bottom: 45%;
    transform: translateX(-50%) scale(1);
  }
`;
const ParentBox = styled(CornerBorderedBox)`
  width: calc(var(--unit) * 11);
  height: calc(var(--unit) * 11);
  padding: 3px;
`;
const ChildBox = styled(CornerBorderedBox)`
  height: 100%;
`;
const Wrapper = styled(Vertical).attrs({ gap: 0, center: true })`
  position: relative;
  width: 100%;
  height: 100%;
  background: radial-gradient(var(--c-black), transparent 80%);
  padding: var(--half-unit);
`;
const AnimatedReward = styled(animated.span)`
  color: var(--c-rivals-win);
  text-shadow: 0.624px 0.356px 6.238px var(--c-rivals-win);
  font-family: var(--rigid-square);
  font-size: var(--fs-24);

  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%) scale(0);
  animation: 500ms ${rewardScaleIn} calc(var(--delay) + 2500ms) ease-out
    forwards;
`;
const AnimatedScore = styled(animated.span)`
  color: var(--c-rivals-loss);
  text-shadow: 0.624px 0.356px 6.238px #6c1600;
  font-family: var(--rigid-square);
  font-size: var(--fs-16);

  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%) scale(0);
  animation: 2000ms ${scoreScaleIn} calc(var(--delay) + 1000ms) ease-out
    forwards;
`;

type Props = { monetaryReward: number | null; score: number; delay?: number };
export const StreakRewardAnimated = ({
  monetaryReward,
  score,
  delay = 0,
}: Props) => {
  const { formatNumber } = useIntlContext();

  const [{ usd }] = useSpring(() => ({
    from: { usd: 0 },
    ...(monetaryReward && { to: { usd: monetaryReward / 100 } }),
    config: { easing: easings.easeOutSine, duration: 2000 },
    delay: delay + 2500,
  }));

  if (!monetaryReward) {
    return null;
  }

  return (
    <ParentBox
      cornerColor="rgba(var(--c-rgb-rivals-loss), 0.4)"
      borderWidth={1}
      cornerSize={15}
      delay={delay}
      scaleIn
    >
      <ChildBox
        cornerColor="rgba(var(--c-rgb-rivals-loss), 0.9)"
        borderWidth={2}
        cornerSize={15}
        delay={delay}
        scaleIn
      >
        <Wrapper gap={0} center>
          <AnimatedReward>
            {usd.to(x =>
              formatNumber(x, {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              })
            )}
          </AnimatedReward>
          <AnimatedScore>
            <FormattedMessage
              id="StreakRewardAnimated.score"
              defaultMessage="{score}pts"
              values={{ score }}
            />
          </AnimatedScore>
        </Wrapper>
      </ChildBox>
    </ParentBox>
  );
};
